import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useExternalReviews } from "../hooks/useExternalReviews";
import ReviewCard from "../components/ReviewCard";
import { Review } from "../types";
import { useWindowVirtualizer, measureElement as rvMeasureElement } from "@tanstack/react-virtual";
import { parseDateToMs } from "../utils/date";

const StoreReviews: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const { reviews, loading, hasMore, error, fetchNextPage, refresh } = useExternalReviews({ storeId });

  const [onlyWithText, setOnlyWithText] = useState<boolean>(() => {
    const saved = localStorage.getItem("onlyWithText");
    return saved !== null ? JSON.parse(saved) : false;
  });
  const handleOnlyWithTextChange = useCallback((value: boolean) => {
    setOnlyWithText(value);
    localStorage.setItem("onlyWithText", JSON.stringify(value));
  }, []);

  const rows = useMemo(() => {
    const base = onlyWithText
      ? reviews.filter((r: Review) => r.text && r.text.trim().length > 0)
      : reviews;
    const seen = new Set<string>();
    const unique = [] as Review[];
    for (const r of base) {
      const key = `${r.platform}:${r.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(r);
    }
    unique.sort(
      (a: Review, b: Review) => parseDateToMs(b.published_at) - parseDateToMs(a.published_at)
    );
    return unique;
  }, [reviews, onlyWithText]);

  const virtualizer = useWindowVirtualizer({
    count: rows.length + (hasMore ? 1 : 0),
    estimateSize: () => 300,
    overscan: 6,
    measureElement: rvMeasureElement,
    getItemKey: (index) => (rows[index] ? `${(rows[index] as any).platform}:${(rows[index] as any).id}` : index),
  });
  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualItem = virtualItems[virtualItems.length - 1];
  const lastIndex = lastVirtualItem ? lastVirtualItem.index : -1;
  const prevLastIndexRef = useRef<number | null>(null);

  useEffect(() => {
    prevLastIndexRef.current = null;
  }, [onlyWithText]);

  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(st > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const fetchReviewsRef = useRef<((page?: number) => Promise<void>) | null>(null);

  // Request de-duplication guards
  const requestedPagesRef = useRef<Set<number>>(new Set());
  const completedPagesRef = useRef<Set<number>>(new Set());
  const queueFetch = useCallback(async (page: number) => {
    if (!fetchReviewsRef.current) return;
    if (requestedPagesRef.current.has(page) || completedPagesRef.current.has(page)) return;
    requestedPagesRef.current.add(page);
    try {
      await fetchReviewsRef.current(page);
      completedPagesRef.current.add(page);
      setCurrentPage(page);
    } finally {
      requestedPagesRef.current.delete(page);
    }
  }, []);

  const fetchReviews = useCallback(
    async (page: number = 1) => {
      // Reset guards on a fresh load
      if (page === 1) {
        requestedPagesRef.current.clear();
        completedPagesRef.current.clear();
      }
      if (page === 1) {
        await refresh();
      } else {
        await fetchNextPage();
      }
      setCurrentPage(page);
    },
    [fetchNextPage, refresh]
  );

  useEffect(() => {
    setCurrentPage(1);
    // use the guarded queue for initial load to avoid StrictMode double-calls
    queueFetch(1);
  }, [queueFetch, storeId]);

  useEffect(() => {
    fetchReviewsRef.current = fetchReviews;
  }, [fetchReviews]);

  // Autoload on scroll
  useEffect(() => {
    if (lastIndex === -1) return;
    if (prevLastIndexRef.current === null) {
      prevLastIndexRef.current = lastIndex;
      return;
    }
    const hasReachedEnd = lastIndex >= rows.length;
    const scrolledFurther = lastIndex > prevLastIndexRef.current;
    if (hasReachedEnd && !loading && hasMore) {
      if (onlyWithText) {
        queueFetch(currentPage + 1);
      } else if (scrolledFurther) {
        queueFetch(currentPage + 1);
      }
    }
    prevLastIndexRef.current = lastIndex;
  }, [lastIndex, rows.length, hasMore, loading, currentPage, onlyWithText, queueFetch]);

  // Auto-fill after enabling "only with text": fetch until at least 20 visible or no more
  const fillingRef = useRef(false);
  useEffect(() => {
    if (!onlyWithText) return;
    if (loading) return;
    if (!hasMore) return;
    if (rows.length >= 20) return;
    if (fillingRef.current) return;
    const fetchMore = async () => {
      try {
        fillingRef.current = true;
        await queueFetch(currentPage + 1);
      } finally {
        fillingRef.current = false;
      }
    };
    fetchMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyWithText, rows.length, hasMore, loading]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <label className="flex items-center space-x-3 px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-100 transition-colors duration-200 h-11">
            <span className="text-sm font-medium text-gray-700">Только с текстом</span>
            <div className="relative inline-block w-10 h-6">
              <input
                type="checkbox"
                checked={onlyWithText}
                onChange={(e) => handleOnlyWithTextChange(e.target.checked)}
                className="sr-only"
              />
              <div className={`absolute inset-0 w-10 h-6 rounded-full transition-colors duration-200 ${onlyWithText ? "bg-blue-600" : "bg-gray-300"}`}>
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ${onlyWithText ? "translate-x-4" : "translate-x-0"}`} />
              </div>
            </div>
          </label>
        </div>
      </div>

      {error && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">{error}</div>
      )}

      {loading && reviews.length === 0 ? (
        <div className="flex justify-center items-center min-h-[240px]">
          <div className="flex flex-col items-center gap-3">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" aria-label="Загрузка" />
            <p className="text-sm text-gray-600">Загрузка отзывов…</p>
          </div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 text-gray-600">Отзывов не найдено</div>
      ) : (
        <div className="mt-2">
          <div className="relative" style={{ height: `${virtualizer.getTotalSize()}px`, width: "100%" }}>
            {virtualItems.map((vi) => {
              const isLoaderRow = vi.index >= rows.length;
              const review = rows[vi.index];
              return (
                <div key={vi.key} className="absolute left-0 right-0" style={{ top: 0, transform: `translateY(${vi.start}px)`, height: `${vi.size}px` }}>
                  <div ref={virtualizer.measureElement} data-index={vi.index} className={`box-border ${vi.index < rows.length - 1 && vi.index < rows.length ? "pb-5" : "pb-0"}`}>
                    {isLoaderRow ? (
                      <div className="flex justify-center items-center py-6">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
                      </div>
                    ) : (
                      <ReviewCard review={review} isNew={false} showAnalysis={false} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={`fixed bottom-6 right-6 z-50 transition-all duration-500 ease-in-out ${showScrollToTop ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-95 pointer-events-none"}`}>
        <button
          onClick={scrollToTop}
          className="group relative p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:ring-offset-2"
          aria-label="Прокрутить наверх"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <svg className="relative w-6 h-6 transform transition-transform duration-300 group-hover:-translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default StoreReviews;


